import type { Survey } from 'types';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { NavLink } from '@/components/NavLink';
import { UserAvatar } from '@/components/UserAvatar';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/Dialogs/ConfirmDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ArrowLeft, Archive, Copy, MoreHorizontal, Pencil, Square, Trash2 } from 'lucide-react';
import { cn } from 'helpers/cn';
import { useSurvey } from '@/state/surveys/useSurvey';
import { type SurveyAction, SURVEY_ACTION_PROMPTS } from '../survey-copy';

type HeaderProps = { survey: Survey };

const BACK_URL = '/surveys/library';

const BACK_CLASS =
  'micro-label border-line text-dim hover:text-foreground hover:bg-hover inline-flex h-8 items-center gap-1.5 ' +
  'rounded-lg border px-2.5 transition-colors duration-150';

/** Running is live (ok); Draft is work in progress (sel); Ended is a plain hairline chip. */
const STATUS_CHIP: Record<Survey['status'], string> = {
  Running: 'border-ok-line text-ok',
  Draft: 'border-line text-dim',
  Ended: 'border-line text-dim',
};

export const Header = ({ survey }: HeaderProps) => {
  const navigate = useNavigate();
  const [pending, setPending] = useState<SurveyAction | null>(null);
  const { startSurvey, endSurvey, duplicateSurvey, archiveSurvey, deleteSurvey } = useSurvey(survey.id);

  const { id, name, status, type, archived } = survey;
  const isDraft = status === 'Draft';
  const isRunning = status === 'Running';

  const run = async () => {
    if (!pending) return;
    if (pending === 'start') await startSurvey(id);
    if (pending === 'end') await endSurvey(id);
    if (pending === 'archive') {
      await archiveSurvey(id);
      navigate(BACK_URL);
    }
    if (pending === 'delete') {
      await deleteSurvey(id);
      navigate(BACK_URL);
    }
    if (pending === 'duplicate') {
      const copy = await duplicateSurvey(id);
      // Straight into the editor: a duplicate exists to be changed, and it is a
      // draft, so there is nothing on its hub to look at yet.
      if (copy) navigate(`/surveys/${copy.id}/edit`);
    }
    setPending(null);
  };

  const Chips = (
    <>
      <span className="micro-label rounded-pill border-line inline-flex border px-2 py-0.5">{type}</span>
      <span className={cn('micro-label rounded-pill inline-flex border px-2 py-0.5', STATUS_CHIP[status])}>
        {status}
      </span>
    </>
  );

  // Who made it, on the ID line: the badge alone, the full name on hover, as in the
  // library's "Created by" column. Nothing when the creator is gone.
  const CreatedBy = survey.createdByUser ? <UserAvatar user={survey.createdByUser} size="xs" tooltip /> : null;

  /*
    Editing is a link, not the landing screen.

    Dropping straight into the wizard is how a Running survey gets its questions
    rewritten mid-flight, which orphans the responses already collected against
    the old question ids. The API refuses that, so the only thing the shortcut
    ever produced was a 409 at save time.
  */
  const EditAction = (
    <Button
      variant="outline"
      onClick={() => navigate(`/surveys/${id}/edit`)}
      className="border-line h-9 rounded-lg bg-transparent"
      // Rendered in both the desktop and the mobile row; the tour finds whichever is shown.
      data-tour="survey-overview-edit"
    >
      <Pencil className="mr-2 h-4 w-4" />
      Edit
    </Button>
  );

  /*
    Ended offers Start again rather than a dead "Ended" button.

    POST /surveys/:id/start only rejects a survey that is already Running, and the
    library row menu has always allowed restarting an ended one. Greying it out
    here would have removed a working action purely to look like the experiment
    hub, and left the two surfaces disagreeing about the same survey.
  */
  // An archived survey is never served, so the API refuses to start one (409): unarchive first.
  const startBlocked = !isRunning && !!archived;

  const PrimaryAction = (
    <Button
      onClick={() => setPending(isRunning ? 'end' : 'start')}
      disabled={startBlocked}
      title={startBlocked ? 'Unarchive this survey to start it' : undefined}
      data-tour="survey-overview-start"
      className={cn(
        'h-9 min-w-[120px] rounded-lg',
        isRunning && 'bg-danger-bg border-danger-line text-danger hover:bg-danger-bg border',
      )}
    >
      {!isRunning && <span>{isDraft ? 'Start Survey' : 'Start again'}</span>}
      {isRunning && (
        <>
          <Square className="mr-2 h-4 w-4" />
          End
        </>
      )}
    </Button>
  );

  const MoreMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label="More options"
          className="text-dim hover:text-foreground hover:bg-hover h-9 w-9 rounded-lg"
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => setPending('duplicate')}>
          <Copy className="mr-2 h-4 w-4" /> Duplicate
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setPending('archive')}>
          <Archive className="mr-2 h-4 w-4" /> {archived ? 'Unarchive' : 'Archive'}
        </DropdownMenuItem>
        {/* A running survey is collecting data; ending it first is deliberate friction. */}
        <DropdownMenuItem onClick={() => setPending('delete')} disabled={isRunning} className="text-danger">
          <Trash2 className="mr-2 h-4 w-4" /> Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <section className="bg-panel border-line rounded-panel border px-4 py-3.5">
      {/* DESKTOP */}
      <div className="hidden items-start justify-between gap-4 md:flex">
        <div className="min-w-0 space-y-2">
          <div className="flex min-w-0 items-center gap-3">
            <NavLink to={BACK_URL} className={BACK_CLASS}>
              <ArrowLeft className="h-3.5 w-3.5" />
              Back
            </NavLink>

            <h1
              className="text-foreground m-0 truncate text-[18px] font-medium leading-none tracking-[-0.01em]"
              title={name}
            >
              {name}
            </h1>

            {Chips}
          </div>

          <div className="flex items-center gap-2">
            <div className="micro-label">ID {id}</div>
            {CreatedBy}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {EditAction}
          {PrimaryAction}
          {MoreMenu}
        </div>
      </div>

      {/* MOBILE */}
      <div className="space-y-3 md:hidden">
        <div className="flex items-center justify-between">
          <NavLink to={BACK_URL} className={BACK_CLASS}>
            <ArrowLeft className="h-3.5 w-3.5" />
            Back
          </NavLink>
          {MoreMenu}
        </div>

        <div className="flex items-center justify-between gap-3">
          {/* min-w-0, or `truncate` has nothing to shrink against and the chips beside
              it get pushed off the right edge instead. */}
          <h1 className="text-foreground m-0 min-w-0 truncate text-title font-medium leading-tight" title={name}>
            {name}
          </h1>
          <div className="flex shrink-0 items-center gap-2">{Chips}</div>
        </div>

        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="micro-label">ID {id}</div>
            {CreatedBy}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {EditAction}
            {PrimaryAction}
          </div>
        </div>
      </div>

      {/* Mounted always, opened by `pending` — see SurveyRowActions for why a
          conditionally-mounted open dialog can leave the page unclickable. */}
      <ConfirmDialog
        open={!!pending}
        title={pending ? SURVEY_ACTION_PROMPTS[pending].title : ''}
        onConfirm={run}
        onCancel={() => setPending(null)}
        confirmLabel={pending === 'end' ? 'End' : undefined}
        confirmVariant={pending === 'end' || pending === 'delete' ? 'destructive' : 'default'}
      >
        {pending && <p className="supporting">{SURVEY_ACTION_PROMPTS[pending].body}</p>}
      </ConfirmDialog>
    </section>
  );
};
